import * as React from 'react';
import {Form, Tag} from 'antd';
import {FormComponentProps} from 'antd/lib/form';
import {Client} from '../api/dtos/dtos';
import locales from '../locales';
import InputFormItem from '../common/form-items/InputFormItem';
import TextAreaFormItem from '../common/form-items/TextAreaFormItem';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import SectionHeaderFormItem from '../common/form-items/SectionHeaderFormItem';
import {addressSuffix, emailSuffix, phoneSuffix, toCombinedAddress} from '../utils/form-utils';
import AutoCompleteFormItem from "../common/form-items/AutoCompleteFormItem";

interface ClientFormProps extends FormComponentProps {
  editable: boolean;
  client?: Client;
  users: string[],
  onFormSubmit: (client: Client) => void;
  onFormCancel: () => void;
}

const FIELD_NAME = 'name';
const FIELD_ADDRESS = 'address';
const FIELD_CITY = 'city';
const FIELD_ZIP = 'zip';
const FIELD_COMMENT = 'comment';
// const FIELD_DISCOUNT_PERCENT = 'discountPercent';
const FIELD_EMAIL = 'email';
const FIELD_TEL = 'tel';
const FIELD_ID_NUMBER = 'idNumber';
const FIELD_MOTHERS_NAME = 'mothersName';
const FIELD_BANK_ACCOUNT = 'bankAccount';
const FIELD_TAX_NUMBER = 'taxNumber';
const FIELD_REG_NUMBER = 'regNumber';
const FIELD_RESPONSIBLE_USER = 'responsibleUser';

class ClientForm extends React.Component<ClientFormProps, {}> {

  public render() {
    const {form, editable, client} = this.props;
    const disabled = !editable;

    return (
      <Form>
        <InputFormItem
          form={form}
          fieldId={FIELD_NAME}
          label={locales.name}
          initialValue={client ? client.name : null}
          required={true}
          disabled={disabled}
        />

        <TextAreaFormItem
          form={form}
          fieldId={FIELD_COMMENT}
          label={locales.comment}
          initialValue={client ? client.comment : null}
          required={false}
          disabled={disabled}
        />

        {/*<NumberFormItem*/}
        {/*form={form}*/}
        {/*fieldId={FIELD_DISCOUNT_PERCENT}*/}
        {/*label={locales.discount}*/}
        {/*initialValue={client ? client.discountPercent : null}*/}
        {/*required={false}*/}
        {/*disabled={disabled}*/}
        {/*min={0}*/}
        {/*max={100}*/}
        {/*>*/}
        {/*<> %</>*/}
        {/*</NumberFormItem>*/}

        <SectionHeaderFormItem title={locales.invoice_data}/>

        <InputFormItem
          form={form}
          fieldId={FIELD_ZIP}
          label={locales.zip}
          initialValue={client ? client.zip : null}
          required={true}
          disabled={disabled}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_CITY}
          label={locales.city}
          initialValue={client ? client.city : null}
          required={true}
          disabled={disabled}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_ADDRESS}
          label={locales.street_house_number}
          initialValue={client ? client.address : null}
          required={true}
          disabled={disabled}
          addonAfter={client && addressSuffix(toCombinedAddress(client))}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_TAX_NUMBER}
          label={locales.taxNumber}
          initialValue={client ? client.taxNumber : null}
          required={false}
          disabled={disabled}
        />

        <SectionHeaderFormItem title={locales.contact}/>

        <InputFormItem
          form={form}
          fieldId={FIELD_EMAIL}
          label={locales.email}
          initialValue={client ? client.email : null}
          required={false}
          type="email"
          rules={[{type: 'email', message: locales.email_invalid,}]}
          disabled={disabled}
          addonAfter={client && emailSuffix(client.email)}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_TEL}
          label={locales.tel}
          initialValue={client ? client.tel : null}
          required={false}
          type="tel"
          disabled={disabled}
          addonAfter={client ? phoneSuffix(client.tel) : null}
        />

        <SectionHeaderFormItem title={locales.otherData}/>

        <InputFormItem
          form={form}
          fieldId={FIELD_ID_NUMBER}
          label={locales.id_number}
          initialValue={client ? client.idNumber : null}
          required={false}
          disabled={disabled}
        >
          <Tag color="purple">{locales.privatePerson}</Tag>
        </InputFormItem>

        <InputFormItem
          form={form}
          fieldId={FIELD_MOTHERS_NAME}
          label={locales.mothers_name}
          initialValue={client ? client.mothersName : null}
          required={false}
          disabled={disabled}
        >
          <Tag color="purple">{locales.privatePerson}</Tag>
        </InputFormItem>

        <InputFormItem
          form={form}
          fieldId={FIELD_BANK_ACCOUNT}
          label={locales.bankAccount}
          initialValue={client ? client.bankAccount : null}
          required={false}
          disabled={disabled}
        >
          <Tag color="blue">{locales.company}</Tag>
        </InputFormItem>

        <InputFormItem
          form={form}
          fieldId={FIELD_REG_NUMBER}
          label={locales.regNumber}
          initialValue={client ? client.regNumber : null}
          required={false}
          disabled={disabled}
        >
          <Tag color="blue">{locales.company}</Tag>
        </InputFormItem>

        <AutoCompleteFormItem
          form={form}
          fieldId={FIELD_RESPONSIBLE_USER}
          label={locales.responsible_user}
          initialValue={client ?  client.responsibleUser : null}
          required={false}
          disabled={disabled}
          allowClear={true}
          dataSource={this.props.users}
          validateOnDataSource={true}
        />

        <SubmitCancelFormItem disabled={disabled} onSubmit={this.onFormSubmit} onCancel={this.onFormCancel}/>
      </Form>
    );
  }

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {client, form, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onFormSubmit({
          id: client ? client.id : null,
          name: values[FIELD_NAME],
          city: values[FIELD_CITY],
          zip: values[FIELD_ZIP],
          address: values[FIELD_ADDRESS],
          comment: values[FIELD_COMMENT],
          // discountPercent: values[FIELD_DISCOUNT_PERCENT],
          email: values[FIELD_EMAIL],
          tel: values[FIELD_TEL],
          bankAccount: values[FIELD_BANK_ACCOUNT],
          taxNumber: values[FIELD_TAX_NUMBER],
          regNumber: values[FIELD_REG_NUMBER],
          idNumber: values[FIELD_ID_NUMBER],
          mothersName: values[FIELD_MOTHERS_NAME],
          responsibleUser: values[FIELD_RESPONSIBLE_USER],
          warning: client ? client.warning : false,
        });
      }
    });
  };
}

export default Form.create<ClientFormProps>()(ClientForm);



// WEBPACK FOOTER //
// ./src/client-details/ClientForm.tsx